import { Link } from "@tanstack/react-router";
import { Pencil, Target } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { usePersonAspirations } from "@/hooks/use-person-aspirations";
import { originLinkSearch } from "@/lib/detail-origin";
import { PersonAspirationsDialog } from "./PersonAspirationsDialog";

/**
 * "Working toward" — the person's aspirational roles, in priority order.
 * Rendered as a labeled subsection INSIDE the Roles panel (below the
 * Current/Future/Previous tabs). The list endpoint 403s for viewers who aren't
 * the person, their manager, or an admin, so the block stays hidden for them
 * (renders only once a successful fetch returns).
 *
 * **Read-only here.** Adding, removing, reordering and re-levelling all live in
 * `PersonAspirationsDialog`, reached from the Manage control — the pickers
 * inline made a narrow summary panel look like a form. Note the control is
 * separate from the panel's own Manage (roles are admin-only; a person owns
 * their own aspirations, so `canManage` is not `isAdmin`).
 *
 * Each aspiration may name the career level being worked toward, so "Senior
 * Solution Architect" is expressible — including for a role the person already
 * holds at a different rung, which is why held roles are labelled "Current"
 * rather than hidden.
 */
export function PersonAspirationsSection({
  personId,
  personName,
  currentRoleIds,
}: {
  personId: string;
  personName: string;
  currentRoleIds: string[];
}) {
  const { data } = usePersonAspirations(personId);
  const [managing, setManaging] = useState(false);

  // Hidden until a successful fetch (forbidden viewers 403 → data undefined).
  if (!data) return null;
  const { aspirations, canManage } = data;

  return (
    <div className="mt-4 space-y-2 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-semibold text-foreground text-sm">
          <Target className="size-4 text-primary" />
          Working toward
        </div>
        {canManage && aspirations.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setManaging(true)}>
            <Pencil className="mr-1 size-3.5" />
            Manage
          </Button>
        )}
      </div>

      {aspirations.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          None set.{" "}
          {canManage && (
            // Mirrors the Roles panel's "Assign one" — with nothing in the list
            // there's no row to hang a Manage button beside.
            <button
              type="button"
              className="font-medium text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
              onClick={() => setManaging(true)}
            >
              Flag a role
            </button>
          )}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {aspirations.map((a) => (
            // Keyed on the entry, not the role: one role can be listed at two
            // levels (Associate now, Staff later).
            <li key={a.id} className="flex items-center gap-2">
              <span
                title={`Priority ${a.rank}`}
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-xs"
              >
                {a.rank}
              </span>
              <Link
                to="/dashboard/roles/$roleId"
                params={{ roleId: a.roleId }}
                search={originLinkSearch("person", personId, personName)}
                className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent"
              >
                <span className="truncate">{a.roleName}</span>
                {/* The level is what makes this "Senior Solution Architect"
                    rather than "Solution Architect", so it reads inline with the
                    name — not as a detached badge. */}
                {a.roleLevelName && (
                  <span className="shrink-0 text-muted-foreground text-xs">
                    · {a.roleLevelName}
                  </span>
                )}
                {currentRoleIds.includes(a.roleId) && (
                  <span
                    title="Already in this role, at a different level"
                    className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 font-medium text-3xs text-muted-foreground uppercase tracking-wide"
                  >
                    Current
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <PersonAspirationsDialog
          open={managing}
          onOpenChange={setManaging}
          personId={personId}
          personName={personName}
        />
      )}
    </div>
  );
}
